import { OFFLINE_STORES, getOfflineStorageDriver } from './offlineStorageAdapter.js';
import { visitaOfflineContextKey, visitaOfflineContextScope } from './visitaOfflineContext.js';
import { formRevision } from './visitaFormUpdatePolicy.js';
import { activoConfigsForGroup } from './formActivoCompatibility.js';

const STORE = OFFLINE_STORES.CONTEXTS;
const sameIds = (left, right) => JSON.stringify(left) === JSON.stringify(right);
const validIds = ids => Array.isArray(ids) && ids.every(id => Number.isSafeInteger(id) && id > 0) && new Set(ids).size === ids.length;
const digest = async value => [...new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(JSON.stringify(value))))]
    .map(byte => byte.toString(16).padStart(2, '0')).join('');

// Los enlaces no equivalen a bytes. Solo un proveedor con read-back puede confirmar cada dependencia.
export function contextFileDependencies(context) {
    const dependencies = [];
    for (const [kind, values] of [['photo', context?.visita?.imagenes], ['document', context?.visita?.adjuntos_documentacion], ['delivery_document', context?.context?.remitos]]) {
        const items = Array.isArray(values) ? values : values && typeof values === 'object' ? Object.values(values) : values ? [values] : [];
        items.forEach((value, index) => {
            if (value != null) dependencies.push({ id: `${kind}:${index}`, kind, status: 'requires_download' });
        });
    }
    return dependencies;
}

export function createVisitasFormPreparation({ user, context, fetchRevisions, canApply = async () => true,
    isAvailable = () => true, dependencies = null, catalog = null, driver = getOfflineStorageDriver(), now = Date.now }) {
    const scope = visitaOfflineContextScope(user), userId = Number(user?.id), empresaId = Number(user?.empresa_id);
    const key = `${visitaOfflineContextKey(user, 0, scope)}:daily-preparation`;
    let disposed = false, pending = null;
    const assertActive = () => { if (disposed || !scope || !isAvailable()) throw Error('Preparación de formularios interrumpida.'); };
    const owned = row => row?.scope === scope && row.userId === userId && row.empresaId === empresaId;
    const catalogCoverage = async (source, prepare = false) => {
        const template = source?.context?.template;
        const required = !!catalog || (template?.fields || []).some(field => activoConfigsForGroup(template, field).length > 0);
        if (!required) return null;
        const sucursalId = Number(source?.context?.visita?.sucursal_id);
        let proof = { complete: false, sucursalId, reason: 'unavailable' };
        try {
            if (!Number.isSafeInteger(sucursalId) || sucursalId < 1 || !catalog?.inspectSucursal) return proof;
            proof = await catalog.inspectSucursal(sucursalId);
            if (prepare && !proof.complete) {
                await catalog.prepare({ sucursalId });
                proof = await catalog.inspectSucursal(sucursalId);
            }
        } catch { assertActive(); proof = { complete: false, sucursalId, reason: 'unavailable' }; }
        assertActive();
        return proof;
    };
    const rowFor = (id, data) => ({ id, kind: 'preparation_manifest', userId, empresaId, visitaId: data.visitaId || 0,
        scope, version: crypto.randomUUID(), templateVersion: data.templateVersion || 'preparation-v1', savedAt: now(), ...data });
    const persist = async (id, data) => {
        assertActive();
        const previous = await driver.get(STORE, id), row = rowFor(id, data);
        if (previous && !owned(previous)) throw Error('El manifiesto anterior requiere revisar su propietario.');
        if (JSON.stringify(row).length * 2 > 8 * 1024 * 1024) throw Error('El manifiesto supera el espacio reservado.');
        const saved = await driver.mutate(STORE, id, current => (current?.version || '') === (previous?.version || '') ? row : null);
        const verified = await driver.get(STORE, id);
        assertActive();
        if (!saved || verified?.version !== row.version || JSON.stringify(verified) !== JSON.stringify(row)) throw Error('No se confirmó el manifiesto de preparación.');
        return row;
    };
    const inspect = async (ids, { day, timezone }) => {
        assertActive();
        if (!validIds(ids)) return { complete: false, formsReady: 0, total: 0 };
        const aggregate = await driver.get(STORE, key);
        if (!owned(aggregate) || aggregate.day !== day || aggregate.timezone !== timezone || !sameIds(aggregate.visitaIds, ids)
            || !Array.isArray(aggregate.visits) || !validIds(aggregate.visits.map(row => row.visitaId))
            || !sameIds(aggregate.visits.map(row => row.visitaId), ids)) {
            return { complete: false, formsReady: 0, total: ids.length };
        }
        const metadata = new Map((await context.prepared()).map(row => [row.visitaId, row]));
        let formsReady = 0, dependenciesPending = 0, catalogsPending = 0;
        for (const manifest of aggregate.visits) {
            const source = await driver.get(STORE, visitaOfflineContextKey(user, manifest.visitaId, scope));
            const local = metadata.get(manifest.visitaId);
            if (owned(manifest) && manifest.status === 'context_ready' && local?.version === manifest.contextVersion && local.revision === manifest.formRevision
                && !local.candidate && !local.hint && manifest.expiresAt > now()) {
                const hash = owned(source) && source.version === manifest.contextVersion ? await digest(source.context) : null;
                if (hash && hash === manifest.contextSha256) formsReady++;
            }
            let checked = manifest.dependencies || [];
            if (dependencies && owned(manifest) && manifest.status === 'context_ready') {
                try { checked = await dependencies.inspect(source, contextFileDependencies(source.context)); }
                catch { checked = (manifest.dependencies || []).map(row => ({ ...row, status: 'requires_download' })); }
            }
            dependenciesPending += dependencies ? checked.filter(row => row.status !== 'ready').length : checked.length;
            const coverage = await catalogCoverage(source);
            if (coverage && !coverage.complete) catalogsPending++;
        }
        assertActive();
        return { complete: formsReady === ids.length && dependenciesPending === 0 && catalogsPending === 0, formsReady, total: ids.length, dependenciesPending,
            ...(catalog || catalogsPending ? { catalogsPending } : {}) };
    };
    const prepare = (ids, { day, timezone, onProgress = () => {} } = {}) => {
        if (pending) return pending;
        pending = (async () => {
            assertActive();
            if (!validIds(ids)) throw Error('La selección de visitas está incompleta.');
            const visits = [];
            for (let offset = 0; offset < ids.length; offset += 50) {
                const batch = ids.slice(offset, offset + 50), remote = await fetchRevisions(batch);
                assertActive();
                const received = [...(remote?.visitas || []).map(row => Number(row.visita_id)), ...(remote?.unavailable_ids || []).map(Number)];
                if (remote?.schema_version !== 1 || Number(remote.user_id) !== userId || Number(remote.empresa_id) !== empresaId
                    || !Array.isArray(remote.visitas) || !Array.isArray(remote.unavailable_ids)
                    || !validIds(received) || received.length !== batch.length || received.some(id => !batch.includes(id))) throw Error('El servidor no confirmó todas las visitas seleccionadas.');
                const metadata = new Map((await context.prepared()).map(row => [row.visitaId, row]));
                for (const visitaId of batch) {
                    assertActive();
                    const sourceKey = visitaOfflineContextKey(user, visitaId, scope);
                    const previousManifest = await driver.get(STORE, `${sourceKey}:preparation`);
                    const previousConfirmed = owned(previousManifest)
                        ? previousManifest.status === 'context_ready' ? Object.fromEntries(['contextVersion', 'contextSha256', 'formRevision', 'expiresAt', 'templateVersion']
                            .map(field => [field, previousManifest[field]])) : previousManifest.previousConfirmed : null;
                    const expected = remote.visitas.find(row => Number(row.visita_id) === visitaId);
                    let item = { visitaId, status: 'pending', dependencies: [], reason: 'unavailable' };
                    if (!expected) await context.revoke(visitaId); // Denegación explícita: retirar autorización, conservar borradores y archivos.
                    if (expected && /^[a-f0-9]{64}$/.test(expected.form_revision || '') && Number.isSafeInteger(expected.responses_version) && expected.responses_version >= 0) {
                        try {
                            let saved = await context.read(visitaId);
                            const matches = value => formRevision(value) === expected.form_revision && Number(value?.visita?.responses_version) === expected.responses_version;
                            const known = metadata.get(visitaId);
                            const before = await driver.get(STORE, sourceKey);
                            const verifiedSnapshot = previousConfirmed?.contextVersion === before?.version && previousConfirmed?.contextSha256
                                && owned(before) && previousConfirmed.contextSha256 === await digest(before.context);
                            if (!matches(saved) || !verifiedSnapshot || known?.candidate || known?.hint) {
                                if (!await canApply(visitaId)) throw Error('Hay trabajo local o edición en curso.');
                                const loaded = await context.load(visitaId);
                                if (loaded.source !== 'server' || loaded.storageError || loaded.reason === 'form_update_pending') throw Error('La revisión todavía no quedó preparada.');
                                saved = await context.read(visitaId);
                            }
                            assertActive();
                            const source = await driver.get(STORE, sourceKey);
                            if (!matches(saved) || !owned(source) || source.kind !== 'context' || formRevision(source.context) !== expected.form_revision
                                || Number(source.context?.visita?.responses_version) !== expected.responses_version || source.context.offline_authorization.expires_at <= now()) throw Error('El formulario cambió durante la preparación.');
                            item = { visitaId, status: 'context_ready', contextVersion: source.version, formRevision: expected.form_revision,
                                contextSha256: await digest(source.context),
                                templateVersion: source.templateVersion, expiresAt: source.context.offline_authorization.expires_at,
                                catalog: await catalogCoverage(source, true),
                                dependencies: dependencies ? await dependencies.prepare(source, contextFileDependencies(source.context)) : contextFileDependencies(source.context) };
                        } catch { assertActive(); item.reason = 'context_pending'; }
                    }
                    if (item.status !== 'context_ready' && previousConfirmed) item.previousConfirmed = previousConfirmed;
                    const manifest = await persist(`${visitaOfflineContextKey(user, visitaId, scope)}:preparation`, item);
                    visits.push(manifest);
                    if (visits.length % 10 === 0 || visits.length === ids.length) onProgress({ formsReady: visits.filter(row => row.status === 'context_ready').length, total: ids.length });
                }
            }
            await persist(key, { day, timezone, visitaIds: ids, visits });
            return inspect(ids, { day, timezone });
        })().finally(() => { pending = null; });
        return pending;
    };
    return { prepare, inspect, dispose() { disposed = true; context.dispose(); } };
}
